package com.arena_web.ui.components.forms

import androidx.compose.runtime.*
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.SportColorScheme
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.UserSelect
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*

private val WEEKDAY_SHORT = arrayOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")

/**
 * Horizontal day-strip date picker. A scrollable run of days — swipe (or drag
 * with a mouse) to scroll, and the active day auto-centres on open; the
 * desktop 7-day window (scrollable=false) keeps its paging arrows. Used by
 * both the Arena calendar filter and the Create-Event flow.
 *
 * [eventStyle] = Create-Event restyle (Figma 8697:91594): narrow #042a67
 * capsules + a light hairline container. Default keeps the original filter look —
 * bright sport-accent on the selected day, wider pills, gradient-border pill.
 * [bare] drops the picker's own surface so it can sit inside a wrapping panel
 * (the Arena calendar dropdown card). [allowPast] also surfaces recent past days.
 */
@Composable
fun DatePicker(
    selectedDate: String,
    onDateSelected: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    required: Boolean = false,
    allowPast: Boolean = false,
    eventStyle: Boolean = false,
    bare: Boolean = false,
    // true  → one horizontally scrollable strip (swipe / arrows page it).
    // false → the classic 7-day window paged purely by the arrows. The desktop
    //         Arena filter keeps this so it always shows exactly 7 across.
    scrollable: Boolean = true,
) {
    val sportColors = LocalSportColors.current
    val today = remember { currentDate() }
    var startOffset by remember { mutableStateOf(0) }
    val scrollId = remember {
        "datepicker-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}"
    }
    val stripDays = remember(today, allowPast) {
        val from = if (allowPast) -28 else 0
        (from..60).map { addDays(today, it) }
    }
    // Day to centre on open: the selected one if it's in range, else today (so a
    // filter with no date set opens on today rather than 4 weeks in the past).
    val centerStr = remember(selectedDate, stripDays) {
        if (stripDays.any { fmtDate(it) == selectedDate }) selectedDate else fmtDate(today)
    }
    LaunchedEffect(scrollId, centerStr) {
        delay(60)
        // Centre the active day by setting scrollLeft directly — NOT
        // scrollIntoView, which also scrolls the surrounding sheet vertically
        // (auto-scrolling the whole sheet down to the picker on open).
        val strip = kotlinx.browser.document.getElementById(scrollId) as? org.w3c.dom.HTMLElement
        val sel = kotlinx.browser.document.getElementById("$scrollId-sel") as? org.w3c.dom.HTMLElement
        if (strip != null && sel != null) {
            val stripRect = strip.getBoundingClientRect()
            val selRect = sel.getBoundingClientRect()
            val selCenter = (selRect.left - stripRect.left) + selRect.width / 2.0
            strip.scrollLeft += selCenter - strip.clientWidth / 2.0
        }
    }

    Column(modifier = modifier.fillMaxWidth().sGap(6)) {
        label?.let {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.styleModifier { property("padding-left", s(4)) }
            ) {
                SpanText(it, modifier = Modifier.sFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text2))
                if (required) {
                    SpanText(" *", modifier = Modifier.sFontSize(14).color(AppColors.Primary))
                }
            }
        }

        // Pill container.
        // bare      → no surface (a wrapping panel supplies it — Arena calendar card).
        // eventStyle→ faint white hairline + subtle blue wash (Figma 8697:91594).
        // default   → the standard gradient-border filter pill.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .then(if (bare) Modifier else Modifier.sBorderRadius(38))
                .styleModifier {
                    if (!bare) {
                        // Same surface as the input fields (gradient border).
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "${AppGradients.InteractiveSecondary} padding-box, " +
                                "linear-gradient(0deg, var(--l-surface-2, var(--l-s2, #09142D)), var(--l-surface-2, var(--l-s2, #09142D))) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        // Scroll strip (mobile): the scroller itself is flush
                        // with the surface edges — the lead-in is inside it.
                        property("padding", if (scrollable) "${s(6)} 0" else "${s(6)} ${s(4)}")
                    }
                    property("box-sizing", "border-box")
                    property("gap", s(4))
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (scrollable) {
                // Scroll container — native horizontal swipe on touch; no
                // paging arrows (mobile-only variant).
                Row(
                    modifier = Modifier
                        .id(scrollId)
                        .flexGrow(1)
                        .sGap(8)
                        .styleModifier {
                            property("overflow-x", "auto")
                            property("overflow-y", "hidden")
                            property("scrollbar-width", "none")
                            property("-ms-overflow-style", "none")
                            property("scroll-behavior", "smooth")
                            property("scroll-snap-type", "x proximity")
                            property("-webkit-overflow-scrolling", "touch")
                            property("min-width", "0")
                            // Inner lead-in so the end pills clear the rounded
                            // container corners while scrolling edge-to-edge.
                            if (!bare) property("padding", "0 ${s(12)}")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    stripDays.forEach { day ->
                        val dateStr = fmtDate(day)
                        DayPill(
                            eventStyle = eventStyle,
                            sportColors = sportColors,
                            dayLabel = WEEKDAY_SHORT[dayOfWeek(day.year, day.month, day.day)],
                            dayNum = day.day.toString(),
                            isSelected = selectedDate == dateStr,
                            elementId = if (dateStr == centerStr) "$scrollId-sel" else null,
                            grow = false,
                            onClick = { onDateSelected(dateStr) },
                        )
                    }
                }
            } else {
                // Classic 7-day window — arrows page by a week; always 7 across.
                val canGoBack = allowPast || startOffset > 0
                val pagedDays = (0 until 7).map { addDays(today, startOffset + it) }
                NavArrow(eventStyle = eventStyle, back = true, enabled = canGoBack) {
                    if (canGoBack) startOffset -= 7
                }
                Row(
                    modifier = Modifier
                        .flexGrow(1)
                        .sGap(8)
                ) {
                    pagedDays.forEach { day ->
                        val dateStr = fmtDate(day)
                        DayPill(
                            eventStyle = eventStyle,
                            sportColors = sportColors,
                            dayLabel = WEEKDAY_SHORT[dayOfWeek(day.year, day.month, day.day)],
                            dayNum = day.day.toString(),
                            isSelected = selectedDate == dateStr,
                            elementId = null,
                            grow = true,
                            onClick = { onDateSelected(dateStr) },
                        )
                    }
                }
                NavArrow(eventStyle = eventStyle, back = false, enabled = true) { startOffset += 7 }
            }
        }
    }
}

/** One day capsule. eventStyle = Figma 8697:91596 (narrow #042a67); else the
 *  original wider sport-accent filter pill. Never shrinks (it's in a scroller). */
@Composable
private fun DayPill(
    eventStyle: Boolean,
    sportColors: SportColorScheme,
    dayLabel: String,
    dayNum: String,
    isSelected: Boolean,
    elementId: String?,
    // true (paged mode) → the pill grows from its base width to share any spare
    // space between the arrows. false (scroll strip) → fixed base width, no shrink.
    grow: Boolean,
    onClick: () -> Unit,
) {
    var hovered by remember { mutableStateOf(false) }
    val selectedTextColor = if (eventStyle) AppColors.PrimaryWhite else sportColors.onPrimary
    // Scroll-strip pills (mobile, grow=false) are wider with bigger day
    // numbers; the desktop 7-day window keeps the original metrics.
    val base = if (eventStyle) (if (grow) 33 else 40) else (if (grow) 56 else 64)
    Column(
        modifier = Modifier
            .then(if (elementId != null) Modifier.id(elementId) else Modifier)
            .sBorderRadius(if (eventStyle) 74 else 100)
            .cursor(Cursor.Pointer)
            .userSelect(UserSelect.None)
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .onClick { onClick() }
            .then(if (eventStyle) Modifier else Modifier.sPadding(14, 0))
            .styleModifier {
                property("scroll-snap-align", "center")
                // grow → never shrink below base, expand to fill; else fixed.
                if (grow) property("flex", "1 0 ${s(base)}")
                else { property("flex-shrink", "0"); property("width", s(base)) }
                if (eventStyle) {
                    property("height", s(54))
                    property("justify-content", "center")
                    // Tighter gap lifts the day number a touch higher.
                    property("gap", s(if (grow) 5 else 3))
                }
                property(
                    "background",
                    when {
                        isSelected -> sportColors.primary.toString()
                        hovered -> "var(--l-hover2, #24395f)"
                        else -> "var(--l-s2, #1b2c52)"
                    }
                )
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            dayLabel,
            modifier = Modifier
                .sFontSize(if (eventStyle) 10 else (if (grow) 14 else 16))
                .fontWeight(FontWeight.Light)
                .sLetterSpacing(if (eventStyle) 0.02 else 0.0)
                .color(if (isSelected) org.jetbrains.compose.web.css.Color(selectedTextColor.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
        )
        SpanText(
            dayNum,
            modifier = Modifier
                .sFontSize(if (eventStyle) (if (grow) 12 else 17) else (if (grow) 16 else 22))
                .fontWeight(FontWeight.Medium)
                .sLetterSpacing(if (eventStyle) 0.012 else 0.0)
                .color(if (isSelected) org.jetbrains.compose.web.css.Color(selectedTextColor.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                .then(
                    when {
                        // Mobile sheet strip: nudge the number up a touch.
                        eventStyle && !grow -> Modifier.styleModifier {
                            property("position", "relative")
                            property("top", "-2px")
                        }
                        eventStyle -> Modifier
                        else -> Modifier.sMarginTop(6)
                    }
                )
        )
    }
}

/** Prev / next chevron. `back` flips the glyph; `enabled` dims + disables it. */
@Composable
private fun NavArrow(eventStyle: Boolean, back: Boolean, enabled: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .sSize(if (eventStyle) 30 else 36)
            .userSelect(UserSelect.None)
            .then(if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() } else Modifier)
            .styleModifier {
                property("flex-shrink", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("opacity", if (enabled) "1" else "0.3")
            },
        contentAlignment = Alignment.Center
    ) {
        Icon(
            src = AppIcons.ChevronForward,
            modifier = Modifier.sSize(if (eventStyle) 16 else 20).then(
                if (back) Modifier.styleModifier { property("transform", "rotate(180deg)") } else Modifier
            )
        )
    }
}

// ── Date utility functions ──

private data class SimpleDate(val year: Int, val month: Int, val day: Int)

private fun fmtDate(d: SimpleDate): String =
    "${d.year}-${d.month.toString().padStart(2, '0')}-${d.day.toString().padStart(2, '0')}"

private fun currentDate(): SimpleDate {
    val now = kotlin.js.Date()
    return SimpleDate(
        year = now.getFullYear(),
        month = now.getMonth() + 1,
        day = now.getDate()
    )
}

private fun addDays(base: SimpleDate, offset: Int): SimpleDate {
    val d = kotlin.js.Date(base.year, base.month - 1, base.day + offset)
    return SimpleDate(
        year = d.getFullYear(),
        month = d.getMonth() + 1,
        day = d.getDate()
    )
}

private fun dayOfWeek(year: Int, month: Int, day: Int): Int {
    val jsDay = kotlin.js.Date(year, month - 1, day).getDay()
    return if (jsDay == 0) 6 else jsDay - 1
}
